import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

function isHttpErrorResponse(error: unknown): error is HttpErrorResponse {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    'url' in error &&
    typeof (error as HttpErrorResponse).status === 'number'
  );
}

/**
 * Builds safe extra context from an HttpErrorResponse (no PII).
 */
function getHttpErrorExtra(response: HttpErrorResponse): Record<string, unknown> {
  const extra: Record<string, unknown> = {
    status: response.status,
    statusText: response.statusText,
    url: response.url
  };
  const body = response.error;
  if (body && typeof body === 'object' && !Array.isArray(body)) {
    if (typeof body.ErrorCode === 'string') extra['errorCode'] = body.ErrorCode;
    if (typeof body.ErrorMessage === 'string') extra['errorMessage'] = body.ErrorMessage;
  }
  return extra;
}

/**
 * Reports a critical error to Sentry in the background. No user-facing side effects.
 * Use in catch blocks for business-critical operations (e.g. registration, tax assessment create/update)
 * so the dev team gets context without changing the UX.
 *
 * HttpErrorResponse is normalized to a clear exception message with HTTP details in extra,
 * so Sentry shows a readable event instead of "Object captured as exception with keys: ...".
 *
 * @param operation Short identifier for the operation (e.g. 'registration', 'tax_assessment_create')
 * @param error The caught error
 * @param context Optional non-PII context (e.g. errorCode) for grouping and debugging
 */
export function reportCriticalError(
  operation: string,
  error: unknown,
  context?: Record<string, string | number | boolean | undefined>
): void {
  try {
    const tags = { operation, critical: 'true' };

    if (isHttpErrorResponse(error)) {
      const message = `${operation} failed: ${error.status} ${error.statusText}${error.url ? ` - ${error.url}` : ''}`;
      const extra = { ...getHttpErrorExtra(error), ...context };
      Sentry.captureException(new Error(message), {
        tags,
        extra
      });
      return;
    }

    if (error instanceof Error) {
      Sentry.captureException(error, {
        tags,
        extra: context ?? {}
      });
      return;
    }

    Sentry.captureException(new Error(String(error)), {
      tags,
      extra: context ?? {}
    });
  } catch {
    // No-op if Sentry is not available (e.g. tests or not initialized)
  }
}
